import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, CreditCard, Phone, ChevronRight } from "lucide-react";
import { KioskViewport, useKioskSpeech } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const IDLE_TIMEOUT = 60;
const BADGE_INTRO_MESSAGE = "Please insert your Blue Badge into the reader located on YOUR LEFT. Place the badge flat into the small letterbox slot. When you are ready, press Start Scan.";

export default function KioskBlueBadgeIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationData } = location.state || {};
  const [animationStep, setAnimationStep] = useState(0);
  const idleTimerRef = useRef(null);
  
  // Central speech controller - speak badge intro guidance
  useKioskSpeech('KioskBlueBadgeIntro', BADGE_INTRO_MESSAGE, !!registrationData);

  useEffect(() => {
    if (!registrationData) {
      navigate("/kiosk/category");
    }
  }, [registrationData, navigate]);

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  // Animation cycle
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep(prev => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleContinue = () => {
    navigate("/kiosk/badge-scan", { state: { registrationData } });
  };

  if (!registrationData) return null;

  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-badge-intro"
        style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
          overflow: 'hidden',
          background: '#0d2137'
        }}
      >
        {/* STANDARDIZED HEADER */}
        <KioskHeader showDateTime={true} showProgress={true} currentStep={3} />

        {/* BACK BUTTON - TOP OF CONTENT */}
        <div style={{ padding: '20px 40px', background: 'white' }}>
          <button 
            onClick={() => navigate("/kiosk/form", { state: { category: registrationData?.category } })}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              background: '#1e3a5f',
              border: 'none',
              cursor: 'pointer'
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={28} color="white" />
            <span style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>Back</span>
          </button>
        </div>

        {/* HERO SECTION */}
        <div style={{
          position: 'relative',
          height: '500px',
          overflow: 'hidden',
          flexShrink: 0
        }}>
          {/* Cityscape Background */}
          <img 
            src="/cityscape.png" 
            alt="Ayr Town Centre"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center bottom'
            }}
          />

          {/* Tracy - Large */}
          <img 
            src="/tracy.png" 
            alt="Tracy - ParkWise Assistant"
            style={{
              position: 'absolute',
              right: '-20px',
              bottom: '-40px',
              height: '580px',
              zIndex: 10
            }}
          />

          {/* Large Speech Bubble */}
          <div style={{
            position: 'absolute',
            top: '30px',
            left: '40px',
            width: '480px',
            background: '#fef9e7',
            borderRadius: '24px',
            padding: '28px 32px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 20
          }}>
            <h3 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#1e3a5f',
              margin: '0 0 16px 0'
            }}>
              Insert Your Blue Badge
            </h3>
            <p style={{
              fontSize: '20px',
              color: '#374151',
              lineHeight: 1.5,
              margin: 0
            }}>
              Please insert your Blue Badge into the reader located on <span style={{ fontWeight: '800', color: '#dc2626' }}>YOUR LEFT</span>. Place the badge flat into the small letterbox slot.
            </p>
            {/* Pointer */}
            <div style={{
              position: 'absolute',
              right: '-22px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '18px solid transparent',
              borderBottom: '18px solid transparent',
              borderLeft: '24px solid #fef9e7'
            }} />
          </div>
        </div>

        {/* INSTRUCTIONS + ANIMATION SECTION */}
        <div style={{
          flex: 1,
          padding: '30px 40px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #e0f2fe 100%)',
          display: 'flex',
          gap: '40px'
        }}>
          {/* Badge Reader Animation */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              background: '#475569',
              borderRadius: '20px',
              padding: '24px',
              border: `4px solid ${animationStep >= 2 ? '#22c55e' : '#3b82f6'}`,
              boxShadow: animationStep >= 2 ? '0 0 40px rgba(34,197,94,0.3)' : '0 0 40px rgba(59,130,246,0.3)',
              transition: 'all 0.5s'
            }}>
              <div style={{
                width: '140px',
                height: '220px',
                background: '#334155',
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Letterbox slot */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: '12px',
                  right: '12px',
                  height: '70px',
                  background: '#1e293b',
                  borderRadius: '8px',
                  border: '2px solid #64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Badge animation */}
                  <div style={{
                    background: '#3b82f6',
                    borderRadius: '6px',
                    transition: 'all 0.7s ease-in-out',
                    width: animationStep === 0 ? '0px' : animationStep === 1 ? '60px' : animationStep === 2 ? '80px' : '0px',
                    height: '50px',
                    opacity: animationStep >= 1 && animationStep <= 2 ? 1 : 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {animationStep >= 1 && animationStep <= 2 && (
                      <span style={{ color: 'white', fontSize: '12px', fontWeight: '700' }}>BADGE</span>
                    )}
                  </div>
                </div>
                
                {/* Top scanner indicator */}
                <div style={{ position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                  <div style={{ width: '24px', height: '6px', background: animationStep === 2 ? '#22c55e' : '#64748b', borderRadius: '3px' }} />
                  <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>Scanner</p>
                </div>
                
                {/* Bottom scanner indicator */}
                <div style={{ position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                  <p style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px' }}>Scanner</p>
                  <div style={{ width: '24px', height: '6px', background: animationStep === 2 ? '#22c55e' : '#64748b', borderRadius: '3px' }} />
                </div>
                
                {/* Status light */}
                <div style={{
                  position: 'absolute',
                  bottom: '60px',
                  right: '12px',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: animationStep >= 2 ? '#22c55e' : '#3b82f6',
                  animation: animationStep < 2 ? 'pulse 1s infinite' : 'none'
                }} />
              </div>
              <p style={{ color: '#94a3b8', fontSize: '14px', textAlign: 'center', marginTop: '16px', fontWeight: '600' }}>
                Badge Reader
              </p>
            </div>
          </div>

          {/* Instructions List */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[
              { num: 1, text: "Locate the badge reader on YOUR LEFT" },
              { num: 2, text: "Insert your badge flat into the small letterbox slot" },
              { num: 3, text: "The reader will scan both sides of your badge automatically" },
              { num: 4, text: "Keep still during the 5-second countdown, then remove badge when instructed" }
            ].map((step) => (
              <div key={step.num} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                background: 'white',
                borderRadius: '16px',
                padding: '20px 24px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>{step.num}</span>
                </div>
                <p style={{ fontSize: '20px', color: '#1e3a5f', margin: 0, lineHeight: 1.4 }}>{step.text}</p>
              </div>
            ))}

            {/* Ready Button */}
            <button
              onClick={handleContinue}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '24px 48px',
                borderRadius: '16px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '28px',
                fontWeight: '700',
                color: 'white',
                background: '#22c55e',
                boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
                marginTop: 'auto'
              }}
              data-testid="ready-scan-button"
            >
              Ready to Scan <ChevronRight size={32} />
            </button>
          </div>
        </div>

        {/* HELP INFO - In content area, no footer */}
        <div style={{
          margin: '24px 40px',
          padding: '20px 32px',
          background: '#1e3a5f',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <Phone size={24} color="#fbbf24" />
          <span style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>Need help? Call </span>
          <span style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>01292 833107</span>
        </div>
      </div>
    </KioskViewport>
  );
}
